// frontend/src/lib/outcomeSettings.ts
import { ContactMethod, ResultStatus, VisitActionFlag } from '../types/script'
import type { CanvasResultSettingsFormState, DefaultResultSettingsDto } from '../types/script'

/**
 * [Spec §5.3, D9] The organizer's Door outcome settings as plain data. Mirrors the backend
 * ResultSettingsPolicy: three locked-on results, the optional door results, and the three governed
 * follow-ups. The editor renders these; nothing here knows about screens.
 */
export type OutcomeItem = { kind: 'result'; value: ResultStatus } | { kind: 'flag'; value: VisitActionFlag }

export const DOOR_LOCKED_RESULTS: readonly ResultStatus[] = [ResultStatus.NoAnswer, ResultStatus.Completed, ResultStatus.Refused]
export const DOOR_OPTIONAL_RESULTS: readonly ResultStatus[] = [
  ResultStatus.Busy, ResultStatus.Moved, ResultStatus.Deceased, ResultStatus.Inaccessible, ResultStatus.LanguageBarrier,
]
export const DOOR_FOLLOW_UPS: readonly VisitActionFlag[] = [
  VisitActionFlag.LeftLiterature, VisitActionFlag.ComeBackLater, VisitActionFlag.ScheduleCallback,
]

export const isLocked = (item: OutcomeItem) => item.kind === 'result' && DOOR_LOCKED_RESULTS.includes(item.value)

export function isOn(row: CanvasResultSettingsFormState, item: OutcomeItem): boolean {
  if (isLocked(item)) return true
  return item.kind === 'result' ? row.allowedResults.includes(item.value) : row.allowedFlags.includes(item.value)
}

const flip = <T,>(xs: T[], x: T) => (xs.includes(x) ? xs.filter(y => y !== x) : [...xs, x])

export function toggle(row: CanvasResultSettingsFormState, item: OutcomeItem): CanvasResultSettingsFormState {
  if (isLocked(item)) return row
  return item.kind === 'result'
    ? { ...row, allowedResults: flip(row.allowedResults, item.value) }
    : { ...row, allowedFlags: flip(row.allowedFlags, item.value) }
}

/** The script's Door row, or one built from the system defaults (a script saved before any Door row existed). */
export function doorRowOrDefault(
  settings: CanvasResultSettingsFormState[], defaults: DefaultResultSettingsDto[] | undefined,
): CanvasResultSettingsFormState {
  const existing = settings.find(s => s.contactMethod === ContactMethod.Door)
  if (existing) return existing
  const d = defaults?.find(x => x.contactMethod === ContactMethod.Door)
  return {
    contactMethod: ContactMethod.Door,
    allowedResults: d ? [...d.defaultResults] : [...DOOR_LOCKED_RESULTS, ...DOOR_OPTIONAL_RESULTS],
    allowedFlags: d ? [...d.defaultFlags] : [...DOOR_FOLLOW_UPS],
  }
}

export function replaceRow(settings: CanvasResultSettingsFormState[], row: CanvasResultSettingsFormState) {
  return settings.some(s => s.contactMethod === row.contactMethod)
    ? settings.map(s => (s.contactMethod === row.contactMethod ? row : s))
    : [...settings, row]
}
